import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Globe, Play, Clock, RefreshCw, CheckCircle, XCircle,
  ToggleLeft, ToggleRight, AlertCircle, Timer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt_duration(s) {
  if (!s) return null;
  if (s < 60) return `${s}s`;
  return `${Math.round(s / 60)} Min.`;
}

function fmt_time(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

export default function WebsiteCheckSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [hour, setHour] = useState(3);
  const [minute, setMinute] = useState(0);
  const [enabled, setEnabled] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/settings/website-check`, { withCredentials: true });
      setSettings(r.data);
      setHour(r.data.hour ?? 3);
      setMinute(r.data.minute ?? 0);
      setEnabled(r.data.enabled ?? false);
      setRunning(r.data.is_running ?? false);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    reload();
    // Poll status every 5s when running
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/website-check/status`, { withCredentials: true });
        setRunning(r.data.is_running);
        if (!r.data.is_running && settings?.is_running) {
          // Job finished → reload full settings
          reload();
          toast.success(`Check abgeschlossen: ${r.data.last_run_updated} Leads aktualisiert`);
        }
        setSettings(prev => prev ? { ...prev, ...r.data } : prev);
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [reload]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/settings/website-check`,
        { enabled, hour: parseInt(hour), minute: parseInt(minute) },
        { withCredentials: true }
      );
      setSettings(r.data);
      toast.success(enabled
        ? `Nacht-Check aktiviert – täglich um ${String(hour).padStart(2,"0")}:${String(minute).padStart(2,"0")} Uhr`
        : "Nacht-Check deaktiviert"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    try {
      await axios.post(`${API}/admin/website-check/run`, {}, { withCredentials: true });
      setRunning(true);
      toast.success("Website-Check gestartet – läuft im Hintergrund");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = [0, 15, 30, 45];

  return (
    <div className="space-y-6 py-4 max-w-2xl">
      {/* Header */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-widest text-foreground mb-1">
          Automatischer Website-Check
        </h2>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Das System prüft automatisch alle Lead-Websites auf Alter, Mobilfreundlichkeit und HTTPS –
          und aktualisiert die Lead-Scores entsprechend. Je älter die Website, desto höher die Priorität.
        </p>
      </div>

      {/* Status Card */}
      <div className="border border-border rounded-sm bg-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {running ? (
              <>
                <RefreshCw size={14} className="animate-spin text-blue-500" />
                <span className="text-sm font-medium text-blue-600">Läuft gerade...</span>
              </>
            ) : settings?.last_run_at ? (
              <>
                <CheckCircle size={14} className="text-emerald-500" />
                <span className="text-sm font-medium text-foreground">Letzter Lauf abgeschlossen</span>
              </>
            ) : (
              <>
                <AlertCircle size={14} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Noch nie ausgeführt</span>
              </>
            )}
          </div>
          <Button
            onClick={handleRunNow}
            disabled={running}
            size="sm"
            variant="outline"
            className="rounded-sm gap-1.5 text-xs"
            data-testid="run-website-check-btn"
          >
            {running ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
            Jetzt ausführen
          </Button>
        </div>

        {settings?.last_run_at && (
          <div className="grid grid-cols-3 gap-3 text-xs border-t border-border pt-3">
            <div>
              <p className="text-muted-foreground mb-0.5">Zeitpunkt</p>
              <p className="font-mono font-medium">{fmt_time(settings.last_run_at)}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Aktualisiert</p>
              <p className="font-mono font-bold text-emerald-600">
                {settings.last_run_updated} Leads
              </p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Dauer</p>
              <p className="font-mono font-medium flex items-center gap-1">
                <Timer size={10} />
                {fmt_duration(settings.last_run_duration_s) || "–"}
                {settings.last_run_errors > 0 && (
                  <span className="text-amber-600 ml-1">· {settings.last_run_errors} Fehler</span>
                )}
              </p>
            </div>
          </div>
        )}

        {settings?.next_run_at && enabled && (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 border-t border-border pt-2">
            <Clock size={11} />
            Nächster Lauf: <span className="font-mono font-medium">{fmt_time(settings.next_run_at)}</span>
          </div>
        )}
      </div>

      {/* Konfiguration */}
      <div className="border border-border rounded-sm bg-card p-4 space-y-4">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Zeitplan konfigurieren</p>

        {/* Enable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Nacht-Check aktivieren</p>
            <p className="text-xs text-muted-foreground mt-0.5">Läuft täglich automatisch zur eingestellten Uhrzeit</p>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className="flex items-center gap-2 transition-colors"
            data-testid="toggle-website-check"
          >
            {enabled
              ? <ToggleRight size={28} className="text-foreground" />
              : <ToggleLeft size={28} className="text-muted-foreground" />
            }
            <span className={`text-xs font-bold uppercase tracking-wider ${enabled ? "text-foreground" : "text-muted-foreground"}`}>
              {enabled ? "An" : "Aus"}
            </span>
          </button>
        </div>

        {/* Time Picker */}
        <div className={`space-y-3 transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Uhrzeit (täglich)</Label>
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-muted-foreground">Stunde</Label>
              <select
                value={hour}
                onChange={e => setHour(parseInt(e.target.value))}
                className="w-20 rounded-sm border border-border bg-background px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground"
                data-testid="hour-select"
              >
                {hourOptions.map(h => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
            <span className="text-lg font-bold mt-4">:</span>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-muted-foreground">Minute</Label>
              <select
                value={minute}
                onChange={e => setMinute(parseInt(e.target.value))}
                className="w-20 rounded-sm border border-border bg-background px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground"
                data-testid="minute-select"
              >
                {minuteOptions.map(m => (
                  <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock size={12} />
              <span>= täglich um <strong className="font-mono text-foreground">{String(hour).padStart(2,"0")}:{String(minute).padStart(2,"0")} Uhr</strong> (Berlin)</span>
            </div>
          </div>

          <div className="bg-muted/40 border border-border rounded-sm p-2.5 text-xs text-muted-foreground">
            <Globe size={11} className="inline mr-1.5 mb-0.5" />
            Der Check prüft alle Leads mit Website-URL auf Alter (Copyright-Jahr, Last-Modified),
            Mobilfreundlichkeit und HTTPS – und aktualisiert die Scores automatisch.
            Empfehlung: <strong>03:00 Uhr</strong> – nachts wenn kein Traffic die Performance beeinflusst.
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-sm gap-1.5"
          data-testid="save-website-check-settings"
        >
          {saving ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle size={13} />}
          Einstellungen speichern
        </Button>
      </div>
    </div>
  );
}
